import { useCallback, useEffect, useState } from "react"

import { useScopedStorageKey } from "@/workspaces/scope"

import { useQueryParams } from "@/hooks/useQueryParams"

import {
  DEFAULT_RUNS_VIEW_MODE,
  isRunsViewMode,
  RUNS_VIEW_MODE,
  type RunsViewMode,
} from "../types/viewMode"

/**
 * Base key; the stored key is scoped to the active organization and workspace.
 * The legacy unscoped `runs-page-view-mode` value is intentionally ignored:
 * migrating it into whichever scope is active would leak one tenant's
 * preference into another, so existing users get a one-time view reset.
 */
export const RUNS_PAGE_VIEW_MODE_STORAGE_KEY = "runs-page-view-mode"

/** Query param that opens the page in a given view, ahead of the saved view. */
export const RUNS_VIEW_MODE_URL_PARAM = "view"

/** The view named in the current URL's view param, if it names one. */
function readRunsViewModeFromUrl(): RunsViewMode | null {
  if (typeof window === "undefined") {
    return null
  }

  const urlViewMode = new URLSearchParams(window.location.search).get(
    RUNS_VIEW_MODE_URL_PARAM,
  )
  return isRunsViewMode(urlViewMode) ? urlViewMode : null
}

interface UseRunsViewModeStateParams {
  hideControls?: boolean
  onViewModeChange?: (viewMode: RunsViewMode) => void
}

/**
 * A view saved under an organization-only key while the workspace context was
 * still empty. The workspace context can fill in later as workspace resolution
 * completes, which remounts the page under the
 * organization+workspace key, so the view is carried here across the remount
 * and written to that final key.
 */
let carriedViewMode: { storageKey: string; viewMode: RunsViewMode } | null =
  null

function isWorkspaceScopedKey(storageKey: string) {
  return storageKey.includes(":ws_")
}

function readCarriedViewMode(storageKey: string | undefined) {
  if (
    !storageKey ||
    !carriedViewMode ||
    !isWorkspaceScopedKey(storageKey) ||
    !storageKey.startsWith(`${carriedViewMode.storageKey}:ws_`)
  ) {
    return null
  }
  return carriedViewMode.viewMode
}

/** Saves a view and, for a non-final key, carries it to the workspace key. */
function persistRunsViewMode(storageKey: string, mode: RunsViewMode) {
  saveRunsViewMode(storageKey, mode)
  carriedViewMode = isWorkspaceScopedKey(storageKey)
    ? null
    : { storageKey, viewMode: mode }
}

function saveRunsViewMode(storageKey: string | undefined, mode: RunsViewMode) {
  if (typeof window === "undefined" || !storageKey) {
    return
  }

  try {
    localStorage.setItem(storageKey, mode)
  } catch {
    // Ignore storage errors and preserve the in-memory state change.
  }
}

function getSavedRunsViewMode(
  storageKey: string | undefined,
  hideControls: boolean,
): RunsViewMode {
  if (hideControls || !storageKey || typeof window === "undefined") {
    return DEFAULT_RUNS_VIEW_MODE
  }

  const carried = readCarriedViewMode(storageKey)
  if (carried) {
    return carried
  }

  try {
    const savedViewMode = localStorage.getItem(storageKey)
    return isRunsViewMode(savedViewMode)
      ? savedViewMode
      : DEFAULT_RUNS_VIEW_MODE
  } catch {
    return DEFAULT_RUNS_VIEW_MODE
  }
}

/**
 * The runs page view mode. A view named in the URL is applied once on load:
 * it becomes the saved view and the param is dropped from the URL, so later
 * toggles persist across reloads and back-navigation.
 */
export function useRunsViewModeState({
  hideControls = false,
  onViewModeChange,
}: UseRunsViewModeStateParams = {}) {
  const { setMultiple: setQueryParams } = useQueryParams()
  const [urlViewMode] = useState<RunsViewMode | null>(() =>
    hideControls ? null : readRunsViewModeFromUrl(),
  )
  const storageKey = useScopedStorageKey(RUNS_PAGE_VIEW_MODE_STORAGE_KEY)
  // A view chosen (or arrived with via URL) before the scope resolves. It is
  // shown immediately, then saved under the scope once there is one.
  const [pendingViewMode, setPendingViewMode] = useState<RunsViewMode | null>(
    urlViewMode,
  )
  const [isUrlParamDropped, setIsUrlParamDropped] = useState(false)
  const [viewModeState, setViewModeState] = useState<{
    storageKey: string | undefined
    viewMode: RunsViewMode
  }>(() => ({
    storageKey,
    viewMode: urlViewMode ?? getSavedRunsViewMode(storageKey, hideControls),
  }))

  // Re-read the saved view when the organization or workspace changes (or
  // resolves). A pending view stays in effect until it is saved.
  if (viewModeState.storageKey !== storageKey) {
    setViewModeState({
      storageKey,
      viewMode:
        pendingViewMode ?? getSavedRunsViewMode(storageKey, hideControls),
    })
  }
  const viewMode = viewModeState.viewMode

  useEffect(() => {
    if (!storageKey || !pendingViewMode) {
      return
    }

    persistRunsViewMode(storageKey, pendingViewMode)
    setPendingViewMode(null)
  }, [pendingViewMode, storageKey])

  // Land a view carried from the organization-only key on the workspace key.
  useEffect(() => {
    const carried = readCarriedViewMode(storageKey)
    if (!storageKey || !carried || hideControls) {
      return
    }

    persistRunsViewMode(storageKey, carried)
  }, [hideControls, storageKey])

  useEffect(() => {
    if (!urlViewMode || isUrlParamDropped || !storageKey) {
      return
    }

    setIsUrlParamDropped(true)
    setQueryParams({ [RUNS_VIEW_MODE_URL_PARAM]: null }, { history: "replace" })
  }, [isUrlParamDropped, setQueryParams, storageKey, urlViewMode])

  const handleViewModeChange = useCallback(
    (mode: RunsViewMode) => {
      setViewModeState({ storageKey, viewMode: mode })
      onViewModeChange?.(mode)
      if (storageKey) {
        setPendingViewMode(null)
        persistRunsViewMode(storageKey, mode)
      } else {
        setPendingViewMode(mode)
      }
    },
    [onViewModeChange, storageKey],
  )

  const handleSwitchToRunsView = useCallback(() => {
    handleViewModeChange(RUNS_VIEW_MODE.RUNS)
  }, [handleViewModeChange])

  const handleSwitchToSimulationsView = useCallback(() => {
    handleViewModeChange(RUNS_VIEW_MODE.SIMULATIONS)
  }, [handleViewModeChange])

  const handleToggleViewMode = useCallback(() => {
    handleViewModeChange(
      viewMode === RUNS_VIEW_MODE.RUNS
        ? RUNS_VIEW_MODE.SIMULATIONS
        : RUNS_VIEW_MODE.RUNS,
    )
  }, [handleViewModeChange, viewMode])

  return {
    viewMode,
    urlViewMode,
    handleViewModeChange,
    handleSwitchToRunsView,
    handleSwitchToSimulationsView,
    handleToggleViewMode,
  }
}
